import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type CarrierCatalogue,
  type OrganisationCarrier,
  type ReplaceOrganisationCarriersRequest,
} from '../client';

/**
 * Stable query-key factory for the static carrier catalogue. Single
 * top-level key — the catalogue is identical for every tenant, so there's
 * no tenant or org slot to encode.
 */
export const carrierCatalogueKeys = {
  all: ['directory', 'carrier-catalogue'] as const,
};

/**
 * Per-org carrier selection caches, scoped by `organisationId` so switching
 * orgs in the TenantSwitcher always refetches.
 */
export const organisationCarrierKeys = {
  all: ['directory', 'organisation-carrier'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-carrier', 'org', organisationId] as const,
};

/**
 * Fetch the static carrier catalogue (ferry / container / shortsea
 * operators). Cached aggressively — the catalogue is bundled JSON on the
 * BE, so a 24h `staleTime` + Infinity `gcTime` is fine.
 */
export function useCarrierCatalogue(
  options?: Omit<UseQueryOptions<CarrierCatalogue, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CarrierCatalogue, Error>({
    queryKey: carrierCatalogueKeys.all,
    queryFn: () => directoryApi.getCarrierCatalogue(),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Fetch every active carrier-selection row for an organisation. Pass `null`
 * for `organisationId` to keep the query disabled while the active org is
 * still resolving.
 */
export function useOrganisationCarriers(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<OrganisationCarrier[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationCarrier[], Error>({
    queryKey: organisationId
      ? organisationCarrierKeys.byOrganisation(organisationId)
      : organisationCarrierKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationCarriers(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation around PUT `/organisationCarriers`. One call replaces the full
 * selection; the BE diffs and auto-creates Supplier parties for any newly
 * added carriers, so consumers of the parties cache (Parties screen,
 * Bookings) should also invalidate to pick up the freshly seeded rows.
 */
export function useReplaceOrganisationCarriers() {
  const qc = useQueryClient();
  return useMutation<OrganisationCarrier[], Error, ReplaceOrganisationCarriersRequest>({
    mutationKey: [...organisationCarrierKeys.all, 'replace'],
    mutationFn: (body) => directoryApi.replaceOrganisationCarriers(body),
    onSuccess: (_rows, vars) => {
      qc.invalidateQueries({
        queryKey: organisationCarrierKeys.byOrganisation(vars.organisationId),
      });
      // BE may have auto-created Supplier parties; nuke the parties cache
      // so the Parties screen (and downstream pickers) pick them up.
      qc.invalidateQueries({ queryKey: ['directory', 'parties'] });
    },
  });
}
